import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthApi, UsersApi, type SsoStatusDto, type UserDto } from '@ata-digital/client-tax-system-api-client';
import axios from 'axios';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { tokenManager, type UserInfo } from '@/lib/auth/tokenManager';
import { createLogger } from '@/utils/logger';
import { authKeys } from './useAuth';

/**
 * Plain axios instance for unauthenticated requests.
 * This bypasses the token refresh interceptors in apiAxiosInstance
 * to prevent SSO status checks from being blocked by auth-related requests.
 */
const plainAxiosInstance = axios.create();

const log = createLogger('useSsoAuth');

/**
 * Query keys for SSO-related queries
 */
export const ssoKeys = {
  all: ['sso'] as const,
  status: () => [...ssoKeys.all, 'status'] as const,
} as const;

/**
 * Hook to check if SSO is enabled for the organization
 * This query is cached and will only refetch when explicitly invalidated
 */
export function useSsoStatus() {
  return useQuery({
    queryKey: ssoKeys.status(),
    queryFn: async (): Promise<SsoStatusDto | null> => {
      log.debug('Fetching SSO status');

      // No authentication needed for SSO status check
      // Use plainAxiosInstance to avoid being blocked by token refresh interceptors
      const authApi = new AuthApi(createApiConfig(''), undefined, plainAxiosInstance);

      const response = await authApi.apiAuthSsoStatusGet();

      // Extract the actual DTO from nested response structure: response.data.data
      const ssoStatus = response.data?.data;

      log.debug('SSO status response:', {
        enabled: ssoStatus?.enabled,
        providerName: ssoStatus?.providerName,
      });

      return ssoStatus || null;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false,
  });
}

/**
 * Interface for SSO login parameters
 */
interface SsoLoginParams {
  returnUrl?: string;
}

/**
 * Initiates SSO login flow via direct navigation.
 *
 * IMPORTANT: This function uses direct browser navigation (window.location.href)
 * instead of an XHR/fetch call. This is required because:
 * 1. The backend sets cookies during the SSO login initiation
 * 2. These cookies must be sent back during the OAuth callback from Azure AD
 * 3. Cookies set on XHR responses are not sent on subsequent top-level navigations
 *    from different origins (Azure AD → our API)
 * 4. By using direct navigation, the entire OAuth flow happens as top-level
 *    navigations, ensuring cookies are properly sent with SameSite=Lax
 *
 * Flow:
 * Browser → API /sso/login (sets cookies, returns 302) → Azure AD → API /sso/callback (cookies sent!)
 */
/**
 * Session storage key for persisting the redirect URL across the SSO OAuth flow.
 * Stored before navigating to Azure AD, read back in the callback route.
 */
const SSO_REDIRECT_KEY = 'sso_redirect_url';

export function getSsoRedirectUrl(): string {
  const url = sessionStorage.getItem(SSO_REDIRECT_KEY);
  sessionStorage.removeItem(SSO_REDIRECT_KEY);
  return url || '/preparer';
}

export function initiateSsoLogin(params: SsoLoginParams = {}): void {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
  const returnUrl = encodeURIComponent(params.returnUrl || '/preparer');

  log.info('Initiating SSO login via direct navigation', { returnUrl: params.returnUrl });

  // Persist the redirect URL so the callback route can navigate there after login
  if (params.returnUrl && params.returnUrl !== '/preparer') {
    sessionStorage.setItem(SSO_REDIRECT_KEY, params.returnUrl);
  }

  // Direct navigation to the SSO login endpoint
  // The backend will set cookies and redirect to Azure AD
  window.location.href = `${apiBaseUrl}/api/auth/sso/login?returnUrl=${returnUrl}`;
}

/**
 * Hook that provides SSO login functionality.
 * Returns an object with a mutate function that initiates SSO login.
 *
 * Note: This doesn't use useMutation because SSO login is a direct navigation,
 * not an async operation that returns data. The page will navigate away
 * before any callback could execute.
 */
export function useSsoLogin() {
  return {
    mutate: (params: SsoLoginParams = {}) => {
      initiateSsoLogin(params);
    },
    // Provide isPending for UI compatibility (always false since we navigate away)
    isPending: false,
  };
}

/**
 * Hook to get SSO logout URL for Azure AD sign-out
 * This should be called when a user who logged in via SSO wants to log out
 */
export function useSsoLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<string | null> => {
      log.info('Initiating SSO logout');

      // Check if user logged in via SSO
      const authMethod = tokenManager.getAuthMethod();
      if (authMethod !== 'sso') {
        log.debug('User did not log in via SSO, skipping Azure AD logout');
        return null;
      }

      const token = tokenManager.getStoredToken();
      const authApi = new AuthApi(createApiConfig(token || ''), undefined, apiAxiosInstance);

      try {
        const response = await authApi.apiAuthSsoLogoutGet();

        // The response contains the Azure AD sign-out URL as a string
        // API returns StringApiResponse with structure: { success?, data?, error?, meta? }
        const logoutUrl = response.data?.data;

        log.debug('SSO logout URL received:', { hasUrl: !!logoutUrl });

        return logoutUrl || null;
      } catch (error) {
        log.error('Failed to get SSO logout URL:', error);
        // Don't throw - fall back to regular logout
        return null;
      }
    },
    onSuccess: (logoutUrl) => {
      // Clear local auth state first
      tokenManager.clearTokens();

      // Clear all cached data
      queryClient.clear();

      // Set auth state to logged out
      queryClient.setQueryData(authKeys.session(), {
        token: null,
        refreshToken: null,
        userType: null,
        isAuthenticated: false,
        user: null,
      });

      // If we have a logout URL, redirect to Azure AD for full sign-out
      if (logoutUrl) {
        log.info('Redirecting to Azure AD for SSO sign-out');
        window.location.href = logoutUrl;
      }
    },
    onError: (error) => {
      log.error('SSO logout failed:', error);
      // Still clear local state even if Azure logout fails
      tokenManager.clearTokens();
      queryClient.clear();
      queryClient.setQueryData(authKeys.session(), {
        token: null,
        refreshToken: null,
        userType: null,
        isAuthenticated: false,
        user: null,
      });
    },
  });
}

/**
 * Hook to handle SSO callback processing
 * Stores tokens and updates auth state after successful SSO authentication
 * Also fetches user profile to populate user data in auth state
 */
export function useSsoCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      token: string;
      refreshToken: string;
      expiresAt?: string;
      isNewUser?: boolean;
    }): Promise<{ success: boolean; isNewUser: boolean; user: UserInfo | null }> => {
      log.info('Processing SSO callback', { isNewUser: params.isNewUser });

      // Store tokens with SSO auth method (without user data initially)
      tokenManager.setTokens({
        authToken: params.token,
        refreshToken: params.refreshToken,
        userType: 'staff', // SSO is only for staff/preparers
        authMethod: 'sso',
      });

      // Fetch user profile using the newly stored token
      let user: UserInfo | null = null;
      try {
        log.debug('Fetching user profile after SSO login');
        const usersApi = new UsersApi(createApiConfig(params.token), undefined, apiAxiosInstance);
        const response = await usersApi.apiUsersProfileGet();

        // Extract user data from nested response: response.data.data
        const userDto: UserDto | undefined = response.data?.data;
        if (userDto) {
          // TODO: Consider creating a shared helper function to extract UserInfo from UserDto
          // This pattern is repeated in useAuth.ts (useStaffLogin, useClientLoginVerify, useTokenRefresh)
          user = {
            id: userDto.id,
            email: userDto.email || undefined,
            firstName: userDto.firstName || undefined,
            lastName: userDto.lastName || undefined,
            fullName: userDto.fullName || undefined,
            role: userDto.role,
          };
          log.info('User profile fetched successfully', { userId: user.id, hasName: !!user.firstName });

          // Update tokenManager with user data
          tokenManager.setTokens({
            authToken: params.token,
            refreshToken: params.refreshToken,
            userType: 'staff',
            authMethod: 'sso',
            user,
          });
        } else {
          log.warn('User profile response missing data');
        }
      } catch (error) {
        // Log but don't fail - SSO login should still succeed even if profile fetch fails
        log.warn('Failed to fetch user profile after SSO login:', error);
      }

      return {
        success: true,
        isNewUser: params.isNewUser || false,
        user,
      };
    },
    onSuccess: (data, variables) => {
      log.info('SSO callback processed successfully', { isNewUser: data.isNewUser, hasUser: !!data.user });

      // Update auth state in cache with user data
      queryClient.setQueryData(authKeys.session(), {
        token: variables.token,
        refreshToken: variables.refreshToken,
        userType: 'staff',
        isAuthenticated: true,
        user: data.user,
      });

      // Invalidate user data to refresh
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: (error) => {
      log.error('SSO callback processing failed:', error);
    },
  });
}
